import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { CATEGORIES, SITE } from "../lib/categories";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#121212]" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-[#121212] text-white">
        <div className="ceg-container py-2 flex items-center justify-between text-xs">
          <span className="hidden sm:block tracking-wider uppercase">
            Chicago · Independent · Updated weekly
          </span>
          <span className="ceg-overline">Est. 2026</span>
        </div>
      </div>

      {/* Masthead */}
      <div className="ceg-container py-5 flex items-center justify-between">
        <Link to="/" className="block group" data-testid="site-logo">
          <div className="font-black tracking-tighter leading-none">
            <span className="text-2xl sm:text-3xl block">Chicago</span>
            <span className="text-3xl sm:text-4xl block -mt-1">
              Electronics<span className="text-[#FF3823]">.</span>
            </span>
            <span className="text-xs uppercase tracking-[0.3em] mt-1 inline-block text-[#5C5C5C]">
              The Guide
            </span>
          </div>
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex items-center border border-[#121212] bg-white px-3 py-2 w-72" data-testid="search-form">
          <Search size={18} className="text-[#121212] mr-2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles..."
            className="bg-transparent flex-1 outline-none text-sm placeholder:text-[#5C5C5C]"
            data-testid="search-input"
          />
        </form>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 border border-[#121212]"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="border-t border-[#121212] bg-white hidden md:block" data-testid="primary-nav">
        <div className="ceg-container flex items-center gap-1 overflow-x-auto py-1">
          {CATEGORIES.map((c) => (
            <NavLink
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`nav-${c.slug}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-2 text-xs uppercase tracking-wider font-semibold transition-colors ${
                  isActive ? "bg-[#121212] text-white" : "text-[#121212] hover:bg-[#121212] hover:text-white"
                }`
              }
            >
              {c.name}
            </NavLink>
          ))}
          <span className="flex-1" />
          <NavLink to="/about" className="px-3 py-2 text-xs uppercase tracking-wider font-semibold hover:underline" data-testid="nav-about">About</NavLink>
          <NavLink to="/contact" className="px-3 py-2 text-xs uppercase tracking-wider font-semibold hover:underline" data-testid="nav-contact">Contact</NavLink>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[#121212] bg-white" data-testid="mobile-nav">
          <form onSubmit={onSearch} className="flex items-center border-b border-[#121212] px-4 py-3">
            <Search size={18} className="mr-2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles..."
              className="bg-transparent flex-1 outline-none text-sm"
              data-testid="mobile-search-input"
            />
          </form>
          <div className="flex flex-col">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                onClick={() => setOpen(false)}
                className="px-4 py-3 border-b border-[#E5E5E5] text-sm font-semibold uppercase tracking-wide"
                data-testid={`mobile-nav-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
            <Link to="/about" onClick={() => setOpen(false)} className="px-4 py-3 border-b border-[#E5E5E5] text-sm uppercase">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="px-4 py-3 text-sm uppercase">Contact</Link>
          </div>
        </div>
      )}
    </header>
  );
}
